import { Icon } from "@/assets/icons";
import { cn } from "@/utils";
import { ja } from "date-fns/locale/ja";
import React, { useRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { MdClose } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangePicker.css";
registerLocale("ja", ja);

type DateRange = {
  start: string;
  end: string;
};

type DateRangePickerProps = {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  className?: string;
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  label,
  required,
  error,
  disabled,
  containerClassName,
  inputClassName,
  className,
}) => {
  // Ref for the date picker
  const refDatePicker = useRef<DatePicker>(null);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    onChange?.({ start: formatDate(start), end: formatDate(end) });

    // Close the date picker when both dates are selected
    if (start && end) {
      refDatePicker.current?.setOpen(false);
    }
  };

  // Clear date range
  const handleClear = () => {
    onChange?.({ start: "", end: "" });
  };

  // Open date picker when clicking the icon
  const handleClickIcon = () => {
    refDatePicker.current?.setOpen(true);
  };

  // Convert iso string to Date object
  const parseDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date; // Check for invalid date
  };

  // Convert Date object to iso string
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return date.toISOString();
  };

  return (
    <div className={cn(containerClassName, "z-[11]")}>
      {label && (
        <label
          className={`
                  absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2] pointer-events-none
                `}
          style={{
            background: disabled
              ? "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)"
              : "#ffffff",
          }}
        >
          {label}
          {required && <span className="text-error_main ml-1">*</span>}
        </label>
      )}

      <div
        className={cn(
          "relative flex items-center border rounded-lg px-3 text-sm leading-5.5 hover:border-charleston_green transition-colors duration-200",
          inputClassName,
          disabled &&
            "bg-gray_100 border-gray_200 text-gray_400 cursor-not-allowed",
          error && "border-error_main"
        )}
      >
        <DatePicker
          ref={refDatePicker}
          dateFormatCalendar='yyyy年MM月'
          swapRange
          selected={parseDate(value?.start)}
          onChange={handleDateChange}
          startDate={parseDate(value?.start)}
          endDate={parseDate(value?.end)}
          // excludeDates={[addDays(new Date(), 1), addDays(new Date(), 5)]}
          selectsRange
          selectsDisabledDaysInRange
          placeholderText="開始              ~   終了"
          className={cn("border-none outline-none ring-0 h-10", className)}
          dateFormat={"YYYY/MM/dd"}
          locale={"ja"}
          dayClassName={(date) =>
            date.getDay() === 0
              ? "text-error_main"
              : date.getDay() === 6
                ? "text-cyan"
                : ""
          }
          weekDayClassName={(date) =>
            date.getDay() === 0
              ? "text-error_main"
              : date.getDay() === 6
                ? "text-cyan"
                : ""
          }
          // inline
          // isClearable
        />

        {(value?.start || value?.end) && (
          <MdClose
            className="size-4 cursor-pointer mr-2"
            onClick={handleClear}
          />
        )}

        <Icon
          name="event-filled"
          className="hover:opacity-70 transition-opacity duration-200 cursor-pointer"
          onClick={handleClickIcon}
        />
      </div>
      {error && <span className="text-error_main">{error}</span>}
    </div>
  );
};

export default DateRangePicker;
